import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  MenuItem,
  Box,
  Chip,
  Avatar,
  Alert,
  CircularProgress,
  IconButton,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import BugReportOutlinedIcon from "@mui/icons-material/BugReportOutlined";
import AutoStoriesOutlinedIcon from "@mui/icons-material/AutoStoriesOutlined";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import DragHandleIcon from "@mui/icons-material/DragHandle";
import KeyboardDoubleArrowUpIcon from "@mui/icons-material/KeyboardDoubleArrowUp";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import { getTaskComments, createTaskComment } from "../../services/taskService";
import CommentThread from "../common/CommentThread";

const TASK_TYPES = ["Task", "Bug", "Story"];
const PRIORITIES = ["Low", "Medium", "High", "Critical"];
const STATUSES = ["To Do", "In Progress", "Done"];

const TASK_TYPE_ICON = {
  Task: <TaskAltOutlinedIcon fontSize="small" sx={{ color: "#2563eb" }} />,
  Bug: <BugReportOutlinedIcon fontSize="small" sx={{ color: "#dc2626" }} />,
  Story: <AutoStoriesOutlinedIcon fontSize="small" sx={{ color: "#16a34a" }} />,
};

const PRIORITY_ICON = {
  Low: <KeyboardDoubleArrowDownIcon fontSize="small" sx={{ color: "#16a34a" }} />,
  Medium: <DragHandleIcon fontSize="small" sx={{ color: "#ca8a04" }} />,
  High: <KeyboardDoubleArrowUpIcon fontSize="small" sx={{ color: "#ea580c" }} />,
  Critical: <PriorityHighIcon fontSize="small" sx={{ color: "#dc2626" }} />,
};

const priorityStyle = (priority) => {
  switch (priority) {
    case "Critical":
      return { background: "#fee2e2", color: "#dc2626" };
    case "High":
      return { background: "#ffedd5", color: "#ea580c" };
    case "Medium":
      return { background: "#fef3c7", color: "#ca8a04" };
    case "Low":
      return { background: "#dcfce7", color: "#16a34a" };
    default:
      return { background: "#f1f5f9", color: "#475569" };
  }
};

const statusAccent = (status) => {
  switch (status) {
    case "To Do":
      return "#94a3b8";
    case "In Progress":
      return "#7c3aed";
    case "Done":
      return "#16a34a";
    default:
      return "#94a3b8";
  }
};

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link", "image"],
    ["clean"]
  ]
};

const quillFormats = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "list",
  "bullet",
  "link",
  "image"
];

function TaskDialog({
  open,
  task,
  projectTeam = [],
  sprints = [],
  allTasks = [],
  defaultSprintId = null,
  isSaving = false,
  currentEmployeeName = "",
  currentEmployeeId = null,
  fullScreen = false,
  onClose,
  onSave,
  onCreateSubtask,
  onEditSubtask,
}) {
  const [form, setForm] = useState({
    title: "",
    description: "",
    task_type: "Task",
    priority: "Medium",
    status: "To Do",
    story_points: "",
    assigned_to: "",
    sprint_id: "",
  });
  const [error, setError] = useState("");
  const [subtaskTitle, setSubtaskTitle] = useState("");
  const [isAddingSubtask, setIsAddingSubtask] = useState(false);
  const [moreFieldsOpen, setMoreFieldsOpen] = useState(true);
  const [detailsOpen, setDetailsOpen] = useState(true);
  const [linkCopied, setLinkCopied] = useState(false);
  const quillRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) {
      return;
    }

    if (task) {
      setForm({
        title: task.title || "",
        description: task.description || "",
        task_type: task.task_type || "Task",
        priority: task.priority || "Medium",
        status: task.status || "To Do",
        story_points: task.story_points ?? "",
        assigned_to: task.assigned_to ?? "",
        sprint_id: task.sprint_id ?? "",
      });
    } else {
      setForm({
        title: "",
        description: "",
        task_type: "Task",
        priority: "Medium",
        status: "To Do",
        story_points: "",
        assigned_to: "",
        sprint_id: defaultSprintId ?? "",
      });
    }
    setError("");
    setSubtaskTitle("");
    setIsAddingSubtask(false);
  }, [open, task, defaultSprintId]);

  const setField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const subtasks = task ? allTasks.filter((t) => t.parent_task_id === task.task_id) : [];

  const handleAddSubtask = async () => {
    if (!subtaskTitle.trim() || isAddingSubtask) {
      return;
    }
    setIsAddingSubtask(true);
    try {
      await onCreateSubtask(task, subtaskTitle.trim());
      setSubtaskTitle("");
    } finally {
      setIsAddingSubtask(false);
    }
  };

  const handleSave = () => {
    if (!form.title.trim()) {
      setError("Task title is required");
      return;
    }

    onSave({
      title: form.title.trim(),
      description: form.description || null,
      task_type: form.task_type,
      status: form.status,
      priority: form.priority,
      story_points: form.story_points === "" ? null : Number(form.story_points),
      assigned_to: form.assigned_to === "" ? null : Number(form.assigned_to),
      sprint_id: form.sprint_id === "" ? null : Number(form.sprint_id),
    });
  };

  const handleCopyLink = () => {
    if (!task) return;
    const link = `${window.location.origin}/board/${task.project_id}/task/${task.task_id}`;
    navigator.clipboard.writeText(link).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 1500);
    });
  };

  return (
    <Dialog
      open={open}
      onClose={isSaving ? undefined : onClose}
      maxWidth={false}
      PaperProps={{
        sx: fullScreen
          ? { width: "100vw", height: "100vh", m: 0, borderRadius: 0 }
          : { width: "90vw", height: "90vh", m: 0 },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 700,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          {task && (
            <>
              <Tooltip title="Open task details">
                <Chip
                  label={`#${task.task_id}`}
                  size="small"
                  onClick={() => navigate(`/board/${task.project_id}/task/${task.task_id}`)}
                  sx={{ cursor: "pointer", fontWeight: 700, bgcolor: "background.default", border: "1px solid", borderColor: "divider" }}
                />
              </Tooltip>
              <Tooltip title={linkCopied ? "Link copied!" : "Copy link"}>
                <IconButton size="small" onClick={handleCopyLink}>
                  <ContentCopyIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            </>
          )}
          {task ? "Update Task" : "Create Task"}
        </Box>

        <IconButton onClick={onClose} disabled={isSaving}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ bgcolor: "background.default", p: 0 }}>
        {error && (
          <Alert severity="error" sx={{ m: 3, mb: 0 }}>
            {error}
          </Alert>
        )}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "minmax(0, 4fr) minmax(0, 1fr)" },
            gap: 3,
            p: { xs: 2.5, md: 3 },
            height: "100%",
          }}
        >
          {/* Left column: Title + Description */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.2 }}>
            {task?.parent_task_id && (
              <Chip
                icon={<SubdirectoryArrowRightIcon fontSize="small" />}
                label={`Subtask of "${task.parent_task_title || "task"}"`}
                size="small"
                sx={{ alignSelf: "flex-start", bgcolor: "background.paper", border: "1px solid", borderColor: "divider" }}
              />
            )}

            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", mb: 0.8 }}>
                Title
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Enter task title"
                value={form.title}
                onChange={(e) => setField("title", e.target.value)}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "background.paper" } }}
              />
            </Box>

            <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", mb: 0.8 }}>
                Description
              </Typography>
              <Box
                sx={(theme) => ({
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  "& .quill": { display: "flex", flexDirection: "column", flex: 1 },
                  "& .ql-toolbar": {
                    borderColor: theme.palette.divider,
                    bgcolor: "background.paper",
                    borderTopLeftRadius: 8,
                    borderTopRightRadius: 8,
                  },
                  "& .ql-container": {
                    flex: 1,
                    overflowY: "auto",
                    borderColor: theme.palette.divider,
                    borderBottomLeftRadius: 8,
                    borderBottomRightRadius: 8,
                  },
                  "& .ql-editor": {
                    bgcolor: "background.paper",
                    color: "text.primary",
                  },
                  "& .ql-editor.ql-blank::before": {
                    color: theme.palette.text.secondary,
                    fontStyle: "normal",
                  },
                  "& .ql-picker-label": {
                    color: theme.palette.text.primary,
                  },
                  "& .ql-picker-options": {
                    bgcolor: "background.paper",
                    color: "text.primary",
                  },
                  "& .ql-stroke": {
                    stroke: theme.palette.text.secondary,
                  },
                  "& .ql-fill": {
                    fill: theme.palette.text.secondary,
                  },
                })}
              >
                <ReactQuill
                  ref={quillRef}
                  className="task-description-editor"
                  value={form.description}
                  onChange={(value) => setField("description", value)}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder="Enter task description"
                  style={{
                    fontSize: 16
                  }}
                />
              </Box>
            </Box>

            {task && !task.parent_task_id && (
              <Box>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", mb: 0.8 }}>
                  Subtasks {subtasks.length > 0 && `(${subtasks.filter((s) => s.status === "Done").length}/${subtasks.length})`}
                </Typography>

                {subtasks.length > 0 && (
                  <Box sx={{ display: "flex", height: 8, borderRadius: 4, overflow: "hidden", bgcolor: "background.default", mb: 1.5 }}>
                    <Box sx={{ width: `${(subtasks.filter((s) => s.status === "Done").length / subtasks.length) * 100}%`, bgcolor: "#16a34a" }} />
                    <Box sx={{ width: `${(subtasks.filter((s) => s.status !== "Done").length / subtasks.length) * 100}%`, bgcolor: "#2563eb" }} />
                  </Box>
                )}

                {subtasks.length > 0 && (
                  <TableContainer
                    component={Paper}
                    sx={{ borderRadius: 2, border: "1px solid", borderColor: "divider", boxShadow: "none", mb: 1.5 }}
                  >
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: "background.default" }}>
                          <TableCell sx={{ fontWeight: 600, fontSize: 12, color: "text.secondary" }}>Title</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: 12, color: "text.secondary" }}>Priority</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: 12, color: "text.secondary" }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: 12, color: "text.secondary" }}>Assignee</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {subtasks.map((subtask) => (
                          <TableRow
                            key={subtask.task_id}
                            hover
                            onClick={() => onEditSubtask(subtask)}
                            sx={{ cursor: "pointer", "& td": { borderBottom: "1px solid", borderColor: "divider" } }}
                          >
                            <TableCell>
                              <Typography sx={{ fontSize: 13, color: "text.primary" }} noWrap>
                                {subtask.title}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                icon={PRIORITY_ICON[subtask.priority]}
                                label={subtask.priority}
                                size="small"
                                sx={{ height: 20, fontSize: 10, fontWeight: 600, ...priorityStyle(subtask.priority) }}
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                icon={<Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: statusAccent(subtask.status) }} />}
                                label={subtask.status}
                                size="small"
                                sx={{ height: 20, fontSize: 10, fontWeight: 600, bgcolor: "background.default", border: "1px solid", borderColor: "divider" }}
                              />
                            </TableCell>
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={0.8}>
                                <Avatar sx={{ width: 22, height: 22, fontSize: 10 }}>
                                  {(subtask.assigned_name || "?").slice(0, 1).toUpperCase()}
                                </Avatar>
                                <Typography sx={{ fontSize: 12, color: "text.secondary" }} noWrap>
                                  {subtask.assigned_name || "Unassigned"}
                                </Typography>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                <Box display="flex" gap={1}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Add a subtask"
                    value={subtaskTitle}
                    disabled={isAddingSubtask}
                    onChange={(e) => setSubtaskTitle(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddSubtask();
                      }
                    }}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "background.paper" } }}
                  />
                  <Button
                    variant="outlined"
                    startIcon={isAddingSubtask ? <CircularProgress size={14} color="inherit" /> : <AddIcon fontSize="small" />}
                    onClick={handleAddSubtask}
                    disabled={isAddingSubtask || !subtaskTitle.trim()}
                    sx={{ textTransform: "none", flexShrink: 0 }}
                  >
                    {isAddingSubtask ? "Adding..." : "Add"}
                  </Button>
                </Box>
              </Box>
            )}

            {task && (
              <CommentThread
                key={`task-${task.task_id}`}
                entityId={task.task_id}
                members={projectTeam}
                currentEmployeeId={currentEmployeeId}
                currentEmployeeName={currentEmployeeName}
                fetchComments={getTaskComments}
                postComment={createTaskComment}
              />
            )}
          </Box>

          {/* Right column: everything else */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.2 }}>
            <Box sx={{ bgcolor: "background.paper", border: "1px solid", borderColor: "divider", borderRadius: 2, overflow: "hidden" }}>
              <Box
                onClick={() => setDetailsOpen((prev) => !prev)}
                sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, py: 1.4, cursor: "pointer" }}
              >
                <Typography sx={{ fontSize: 11, fontWeight: 800, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.5 }}>
                  Details
                </Typography>
                <IconButton size="small">
                  <ExpandMoreIcon
                    fontSize="small"
                    sx={{ transform: detailsOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.15s ease" }}
                  />
                </IconButton>
              </Box>

              <Collapse in={detailsOpen}>
                <Box sx={{ px: 2, pb: 2, display: "flex", flexDirection: "column", gap: 2 }}>
                  <Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", mb: 0.8 }}>
                      Type
                    </Typography>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      value={form.task_type}
                      onChange={(e) => setField("task_type", e.target.value)}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "background.default" } }}
                    >
                      {TASK_TYPES.map((type) => (
                        <MenuItem key={type} value={type}>
                          <Box display="flex" alignItems="center" gap={1}>
                            {TASK_TYPE_ICON[type]}
                            {type}
                          </Box>
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>

                  <Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", mb: 0.8 }}>
                      Priority
                    </Typography>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      value={form.priority}
                      onChange={(e) => setField("priority", e.target.value)}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "background.default" } }}
                    >
                      {PRIORITIES.map((priority) => (
                        <MenuItem key={priority} value={priority}>
                          <Box display="flex" alignItems="center" gap={1}>
                            {PRIORITY_ICON[priority]}
                            {priority}
                          </Box>
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>

                  {task && (
                    <Box>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", mb: 0.8 }}>
                        Status
                      </Typography>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        value={form.status}
                        onChange={(e) => setField("status", e.target.value)}
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "background.default" } }}
                      >
                        {STATUSES.map((status) => (
                          <MenuItem key={status} value={status}>{status}</MenuItem>
                        ))}
                      </TextField>
                    </Box>
                  )}

                  <Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", mb: 0.8 }}>
                      Reporter
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      disabled
                      value={task ? (task.created_by_name || "Unknown") : (currentEmployeeName || "You")}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "background.default" } }}
                    />
                  </Box>

                  <Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", mb: 0.8 }}>
                      Assignee
                    </Typography>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      value={form.assigned_to}
                      onChange={(e) => setField("assigned_to", e.target.value)}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "background.default" } }}
                    >
                      <MenuItem value="">Unassigned</MenuItem>
                      {projectTeam.map((member) => (
                        <MenuItem key={member.employee_id} value={member.employee_id}>
                          {member.employee_name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>
                </Box>
              </Collapse>
            </Box>

            <Box sx={{ bgcolor: "background.paper", border: "1px solid", borderColor: "divider", borderRadius: 2, overflow: "hidden" }}>
              <Box
                onClick={() => setMoreFieldsOpen((prev) => !prev)}
                sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, py: 1.4, cursor: "pointer" }}
              >
                <Typography sx={{ fontSize: 11, fontWeight: 800, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.5 }}>
                  More Fields
                </Typography>
                <IconButton size="small">
                  <ExpandMoreIcon
                    fontSize="small"
                    sx={{ transform: moreFieldsOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.15s ease" }}
                  />
                </IconButton>
              </Box>

              <Collapse in={moreFieldsOpen}>
                <Box sx={{ px: 2, pb: 2, display: "flex", flexDirection: "column", gap: 2 }}>
                  <Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", mb: 0.8 }}>
                      Story Points
                    </Typography>
                    <TextField
                      type="number"
                      fullWidth
                      size="small"
                      placeholder="e.g. 3"
                      value={form.story_points}
                      onChange={(e) => setField("story_points", e.target.value)}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "background.default" } }}
                    />
                  </Box>

                  <Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", mb: 0.8 }}>
                      Sprint
                    </Typography>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      value={form.sprint_id}
                      onChange={(e) => setField("sprint_id", e.target.value)}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "background.default" } }}
                    >
                      <MenuItem value="">Backlog</MenuItem>
                      {sprints.map((sprint) => (
                        <MenuItem key={sprint.sprint_id} value={sprint.sprint_id}>
                          {sprint.sprint_name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>
                </Box>
              </Collapse>
            </Box>
          </Box>
        </Box>
      </DialogContent>


      <DialogActions sx={{ px: 3, py: 2, bgcolor: "background.paper", borderTop: "1px solid", borderColor: "divider" }}>
        <Button onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>

        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isSaving}
          startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {isSaving ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default TaskDialog;
